import React, { useState, useRef } from 'react';
import {
  MessageCircle,
  MicOff,
  X,
  Loader,
  Save,
  Database
} from 'lucide-react';
import '../styles/simple-voice-chat.css';
import audiosChatService from '../services/audiosChatService';
import { supabase } from '../lib/supabase';
import { getRealtimeSessionUrl } from '../config/api';
import EnvironmentDetector from '../utils/environmentDetector';

const SimpleVoiceChat = ({ 
  isOpen, 
  onClose, 
  materia, 
  aluno 
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  // Estados para o sistema WebRTC
  const [peerConnection, setPeerConnection] = useState(null);
  const [audioStream, setAudioStream] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [dataChannel, setDataChannel] = useState(null);
  
  // Estados para histórico de mensagens
  const [messages, setMessages] = useState([]);
  
  // Estado para controlar avatar animado
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [currentUserMessage, setCurrentUserMessage] = useState('');
  const [currentAiMessage, setCurrentAiMessage] = useState('');
  
  // Estados para salvamento de áudio
  const [chatId, setChatId] = useState(null);
  const [currentMensagemId, setCurrentMensagemId] = useState(null);
  const [audioRecorder, setAudioRecorder] = useState(null);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [isSavingAudio, setIsSavingAudio] = useState(false);
  
  // Refs para captura de áudio
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Estados para sistema híbrido (NOVO - não afeta funcionalidade existente)
  const [chatMethod, setChatMethod] = useState(null);
  const [isDetectingEnvironment, setIsDetectingEnvironment] = useState(false);

  // Fechar popup
  const handleClose = () => {
    if (isRecording) {
      stopConversation();
    }
    onClose();
  };

  // NOVO: Detectar melhor método de chat (não afeta funcionalidade existente)
  const detectChatMethod = async () => {
    setIsDetectingEnvironment(true);
    try {
      const method = await EnvironmentDetector.getBestChatMethod();
      setChatMethod(method);
      console.log('🔍 Método de chat detectado:', method);
      return method;
    } catch (error) {
      console.error('❌ Erro ao detectar método:', error);
      // Fallback para WebRTC (mantém funcionalidade original)
      const fallbackMethod = {
        method: 'webrtc',
        endpoint: getRealtimeSessionUrl(),
        description: 'WebRTC (Fallback)'
      };
      setChatMethod(fallbackMethod);
      return fallbackMethod;
    } finally {
      setIsDetectingEnvironment(false);
    }
  };

  // NOVO: Método HTTP para deploy (fallback não-invasivo)
  const startHttpChat = async () => {
    try {
      setError(null);
      setIsProcessing(true);
      console.log('🌐 Iniciando chat HTTP (deploy mode)...');

      // Verificar microfone
      const micAvailable = await EnvironmentDetector.testMicrophoneAccess();
      if (!micAvailable) {
        throw new Error('Microfone não disponível');
      }

      // Inicializar gravação
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);

      const recorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processHttpAudio(audioBlob);
        audioChunksRef.current = [];
      };

      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setIsConnected(true);
      
      console.log('✅ Chat HTTP iniciado - clique para gravar');
      
    } catch (error) {
      console.error('❌ Erro no chat HTTP:', error);
      setError(`Erro no chat HTTP: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // NOVO: Processar áudio via HTTP
  const processHttpAudio = async (audioBlob) => {
    try {
      setIsProcessing(true);
      console.log('🎤 Processando áudio via HTTP...');

      // Converter para base64
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioBase64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));

      // Enviar para API
      const response = await fetch('/api/chat/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          audioData: audioBase64,
          materia,
          aluno
        })
      });

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Resposta HTTP recebida');
        
        // Adicionar mensagens ao histórico
        if (result.transcription) {
          setMessages(prev => [...prev, {
            id: Date.now(),
            type: 'user',
            content: result.transcription,
            timestamp: new Date().toLocaleTimeString(),
            method: 'http'
          }]);
        }

        if (result.response) {
          setMessages(prev => [...prev, {
            id: Date.now() + 1,
            type: 'assistant',
            content: result.response,
            timestamp: new Date().toLocaleTimeString(),
            method: 'http'
          }]);
        }

        // Reproduzir áudio da resposta
        if (result.audioBase64) {
          const audioData = `data:audio/mp3;base64,${result.audioBase64}`;
          const audio = new Audio(audioData);
          audio.play().catch(err => console.error('Erro ao reproduzir áudio:', err));
        }
      }

    } catch (error) {
      console.error('❌ Erro ao processar áudio HTTP:', error);
      setError(`Erro ao processar áudio: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // NOVO: Função principal que escolhe o método automaticamente
  const startSmartConversation = async () => {
    try {
      // Detectar melhor método
      const method = await detectChatMethod();
      
      if (method.method === 'webrtc') {
        console.log('🎯 Usando WebRTC (funcionalidade original)');
        return startConversation(); // Chama função original
      } else {
        console.log('🌐 Usando HTTP fallback (deploy mode)');
        return startHttpChat(); // Usa novo método
      }
    } catch (error) {
      console.error('❌ Erro ao iniciar conversa inteligente:', error);
      // Fallback para método original
      return startConversation();
    }
  };

  // Iniciar conversa com WebRTC (FUNCIONALIDADE ORIGINAL PRESERVADA)
  const startConversation = async () => {
    try {
      setError(null);
      setIsProcessing(true);
      
      console.log('💬 Iniciando teste de conversa com IA...');
      
      // 1. Obter sessão efêmera da OpenAI
      const apiUrl = getRealtimeSessionUrl();
      console.log('📡 Conectando ao servidor:', apiUrl);

      const sessionResponse = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (!sessionResponse.ok) {
        const errorText = await sessionResponse.text();
        console.error('❌ Erro do servidor:', errorText);
        throw new Error('Servidor de áudio não disponível');
      }

      const sessionData = await sessionResponse.json();
      const EPHEMERAL_KEY = sessionData.client_secret.value;
      console.log('✅ Sessão efêmera obtida:', sessionData.id);
      
      // 2. Criar conexão WebRTC
      const pc = new RTCPeerConnection();
      setPeerConnection(pc);
      
      // 3. Configurar reprodução de áudio remoto
      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      audioEl.controls = false;
      audioEl.volume = 1.0;
      audioEl.muted = false;
      audioEl.style.display = 'none';
      document.body.appendChild(audioEl);
      
      pc.ontrack = e => {
        console.log('🎵 Recebendo track de áudio:', e.track.kind);
        if (e.streams && e.streams[0]) {
          console.log('🔊 Configurando stream de áudio da IA');
          audioEl.srcObject = e.streams[0];
          
          // Forçar reprodução e controlar avatar
          audioEl.play().then(() => {
            console.log('✅ Áudio da IA reproduzindo');
            setIsAISpeaking(true); // Mostrar avatar falando
          }).catch(err => {
            console.error('❌ Erro ao reproduzir áudio:', err);
          });
          
          // Detectar quando áudio da IA termina
          audioEl.addEventListener('ended', () => {
            console.log('🔇 Áudio da IA terminou');
            setIsAISpeaking(false); // Parar avatar falando
          });
          
          // Detectar quando áudio da IA pausa
          audioEl.addEventListener('pause', () => {
            console.log('⏸️ Áudio da IA pausado');
            setIsAISpeaking(false); // Parar avatar falando
          });
        }
      };
      
      // 4. Obter microfone e configurar gravação
      const micStream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      setAudioStream(micStream);
      pc.addTrack(micStream.getTracks()[0]);
      console.log('✅ Microfone conectado');
      
      // Configurar MediaRecorder para capturar áudio do usuário
      const recorder = new MediaRecorder(micStream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current = recorder;
      setAudioRecorder(recorder);
      
      // 5. Configurar data channel
      const dc = pc.createDataChannel('oai-events');
      setDataChannel(dc); // Salvar referência do data channel

      dc.addEventListener('open', () => {
        console.log('✅ Data channel aberto');
        setIsConnected(true);
      });

      dc.addEventListener('close', () => {
        console.log('🔌 Data channel fechado');
        setIsConnected(false);
      });
      
      dc.addEventListener('message', async (e) => {
        console.log('📨 Mensagem da IA:', e.data);
        try {
          const message = JSON.parse(e.data);
          console.log('🔍 Tipo de mensagem:', message.type);
          
          if (message.type === 'session.created') {
            console.log('✅ Sessão criada com sucesso');
            // Inicializar chat se necessário
            await inicializarChat();
          } else if (message.type === 'session.updated') {
            console.log('✅ Sessão atualizada');
          } else if (message.type === 'response.created') {
            console.log('✅ Resposta criada');
            // Iniciar nova mensagem da IA
            setCurrentAiMessage('');
          } else if (message.type === 'response.output_item.added') {
            console.log('✅ Item de resposta adicionado');
          } else if (message.type === 'response.audio.delta') {
            console.log('🔊 Recebendo áudio da IA');
            // Aqui podemos capturar chunks de áudio da IA se necessário
          } else if (message.type === 'response.audio.done') {
            console.log('✅ Áudio da IA finalizado');
            // Salvar áudio da IA se capturado
            await salvarAudioIA();
          } else if (message.type === 'conversation.item.input_audio_transcription.completed') {
            // Transcrição do áudio do usuário
            const transcription = message.transcript;
            console.log('📝 Transcrição do usuário:', transcription);
            setCurrentUserMessage(transcription);
            
            // Criar mensagem no banco e salvar áudio
            const mensagemId = await salvarMensagemUsuario(transcription);
            if (mensagemId) {
              await salvarAudioUsuario(mensagemId, transcription);
            }
            
            // Adicionar mensagem do usuário ao histórico
            setMessages(prev => [...prev, {
              id: Date.now(),
              type: 'user',
              content: transcription,
              timestamp: new Date().toLocaleTimeString(),
              hasAudio: true
            }]);
          } else if (message.type === 'response.output_item.added' && message.item?.type === 'message') {
            // Resposta da IA (texto)
            if (message.item.content && message.item.content[0]?.transcript) {
              const aiResponse = message.item.content[0].transcript;
              console.log('📝 Resposta da IA:', aiResponse);
              setCurrentAiMessage(aiResponse);
              
              // Salvar mensagem da IA no banco
              const mensagemId = await salvarMensagemIA(aiResponse);
              setCurrentMensagemId(mensagemId);
              
              // Adicionar resposta da IA ao histórico
              setMessages(prev => [...prev, {
                id: Date.now() + 1,
                type: 'assistant',
                content: aiResponse,
                timestamp: new Date().toLocaleTimeString(),
                hasAudio: true
              }]);
            }
          } else if (message.type === 'response.text.delta') {
            // Texto incremental da IA
            if (message.delta) {
              setCurrentAiMessage(prev => prev + message.delta);
            }
          } else if (message.type === 'response.text.done') {
            // Texto da IA finalizado
            const finalText = currentAiMessage;
            if (finalText.trim()) {
              setMessages(prev => [...prev, {
                id: Date.now() + 1,
                type: 'assistant',
                content: finalText,
                timestamp: new Date().toLocaleTimeString()
              }]);
            }
          } else if (message.type === 'error') {
            console.error('❌ Erro da OpenAI:', message.error);
          }
        } catch (err) {
          console.log('📨 Mensagem não-JSON:', e.data);
        }
      });
      
      // 6. Criar oferta SDP
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      
      // 7. Enviar oferta para OpenAI usando o token efêmero
      const baseUrl = 'https://api.openai.com/v1/realtime';
      const model = 'gpt-4o-realtime-preview-2024-12-17';
      const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${EPHEMERAL_KEY}`,
          'Content-Type': 'application/sdp'
        },
      });
      
      if (!sdpResponse.ok) {
        throw new Error(`Erro na conexão WebRTC: ${sdpResponse.status}`);
      }
      
      // 8. Configurar resposta SDP
      const answer = {
        type: 'answer',
        sdp: await sdpResponse.text(),
      };
      await pc.setRemoteDescription(answer);
      
      console.log('✅ Conexão WebRTC estabelecida');
      setIsRecording(true);
      
      // Iniciar gravação de áudio
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.start(1000); // Capturar chunks a cada 1 segundo
        console.log('🎤 Gravação de áudio iniciada');
      }
      
      // Aguardar data channel abrir e enviar configuração inicial
      dc.addEventListener('open', () => {
        console.log('📤 Configurando sessão de áudio...');
        
        // Criar contexto educacional personalizado (SINCRONIZADO COM CHAT DE TEXTO)
        const alunoNome = aluno?.nome || 'Aluno';
        const materiaNome = materia || 'a matéria';
        const anoEscolar = aluno?.anos_escolares?.nome || 'ensino fundamental';
        const escolaNome = aluno?.escolas?.nome || 'escola';
        
        const contextoEducacional = `Você é um tutor especializado em ${materiaNome} para alunos do ensino fundamental.

INFORMAÇÕES DO ALUNO:
- Nome: ${alunoNome}
- Ano Escolar: ${anoEscolar}
- Escola: ${escolaNome}
- Matéria: ${materiaNome}

INSTRUÇÕES GERAIS:
- Use linguagem clara e apropriada para a idade do aluno
- Forneça exemplos práticos e relacionados ao contexto escolar
- Seja encorajador e paciente
- IMPORTANTE: Você APENAS pode ensinar sobre ${materiaNome}. Se o aluno perguntar sobre outros assuntos ou matérias diferentes, responda educadamente: "Desculpe, mas eu só posso ensinar sobre ${materiaNome}. Vamos nos atentar aos estudos de ${materiaNome}."
- Use o nome do aluno ocasionalmente para tornar a conversa mais pessoal
- Seja educativo mas também divertido e envolvente
- SEMPRE responda em áudio usando sua voz clara e didática
- Cumprimente o aluno pelo nome: "${alunoNome}"
- Fale em português brasileiro

Lembre-se: Você está conversando com um aluno do ${anoEscolar} que quer aprender ${materiaNome}. Mantenha sempre o foco nesta matéria específica.`;

        // Configurar sessão para áudio
        dc.send(JSON.stringify({
          type: 'session.update',
          session: {
            modalities: ['text', 'audio'],
            instructions: contextoEducacional,
            voice: 'ash',
            input_audio_format: 'pcm16',
            output_audio_format: 'pcm16',
            turn_detection: {
              type: 'server_vad',
              threshold: 0.5,
              prefix_padding_ms: 300,
              silence_duration_ms: 500
            },
            tools: [],
            tool_choice: 'none',
            temperature: 0.8,
            max_response_output_tokens: 4096
          }
        }));
        
        // Enviar mensagem inicial com contexto prioritário
        setTimeout(() => {
          console.log('📤 Iniciando conversa com contexto educacional...');
          
          // Primeiro, estabelecer o contexto educacional como mensagem do sistema
          dc.send(JSON.stringify({
            type: 'conversation.item.create',
            item: {
              type: 'message',
              role: 'system',
              content: [{
                type: 'input_text',
                text: contextoEducacional
              }]
            }
          }));
          
          // IA fala primeiro - saudação automática sem esperar o usuário
          setTimeout(() => {
            console.log('📤 IA iniciando conversa automaticamente...');
            dc.send(JSON.stringify({
              type: 'response.create',
              response: {
                modalities: ['audio'],
                instructions: `Seguindo exatamente o contexto educacional estabelecido, cumprimente ${alunoNome} de forma calorosa pelo nome e se apresente como tutor especializado em ${materiaNome}. Explique que está aqui para ajudar com estudos de ${materiaNome} e pergunte como pode ajudar hoje. Seja proativo e acolhedor na saudação inicial.`
              }
            }));
          }, 500);
          
        }, 1000);
      });

      setError(null);
      
    } catch (error) {
      console.error('❌ Erro na conversa:', error);
      setError(`❌ Erro na Conversa: ${error.message}`);
      stopConversation();
    } finally {
      setIsProcessing(false);
    }
  };

  // Funções para gerenciar chat e mensagens
  const inicializarChat = async () => {
    try {
      console.log('🔄 Inicializando chat para matéria:', materia);
      
      if (!aluno?.id) {
        console.error('❌ ID do aluno não encontrado');
        return null;
      }
      
      // Criar ou buscar chat existente
      const { data: chatExistente, error: chatError } = await supabase
        .from('chats')
        .select('*')
        .eq('aluno_id', aluno.id)
        .eq('materia', materia)
        .eq('status', 'ativo')
        .order('data_criacao', { ascending: false })
        .limit(1)
        .single();
      
      if (chatExistente && !chatError) {
        console.log('✅ Chat existente encontrado:', chatExistente.id);
        setChatId(chatExistente.id);
        return chatExistente.id;
      }
      
      // Criar novo chat
      const novoChat = {
        aluno_id: aluno.id,
        materia: materia,
        escola: aluno.escolas?.nome || '',
        ano_escolar: aluno.anos_escolares?.nome || '',
        periodo: aluno.periodo || '',
        titulo: `Chat de ${materia} - ${new Date().toLocaleDateString()}`,
        status: 'ativo'
      };
      
      const { data: chatCriado, error: criarError } = await supabase
        .from('chats')
        .insert([novoChat])
        .select()
        .single();
      
      if (criarError) {
        console.error('❌ Erro ao criar chat:', criarError);
        return null;
      }
      
      console.log('✅ Novo chat criado:', chatCriado.id);
      setChatId(chatCriado.id);
      return chatCriado.id;
      
    } catch (error) {
      console.error('❌ Erro ao inicializar chat:', error);
      return null;
    }
  };
  
  const salvarMensagemUsuario = async (transcricao) => {
    try {
      if (!chatId || !aluno?.id) {
        console.error('❌ Chat ID ou Aluno ID não encontrado');
        return null;
      }
      
      const mensagem = {
        chat_id: chatId,
        tipo_mensagem: 'user',
        conteudo: transcricao,
        tem_audio: true
      };
      
      const { data, error } = await supabase
        .from('mensagens_chat')
        .insert([mensagem])
        .select()
        .single();
      
      if (error) {
        console.error('❌ Erro ao salvar mensagem do usuário:', error);
        return null;
      }
      
      console.log('✅ Mensagem do usuário salva:', data.id);
      return data.id;
      
    } catch (error) {
      console.error('❌ Erro ao salvar mensagem:', error);
      return null;
    }
  };
  
  const salvarMensagemIA = async (resposta) => {
    try {
      if (!chatId) {
        console.error('❌ Chat ID não encontrado');
        return null;
      }
      
      const mensagem = {
        chat_id: chatId,
        tipo_mensagem: 'assistant',
        conteudo: resposta,
        tem_audio: true,
        modelo_ia: 'gpt-4o-realtime-preview-2024-12-17'
      };
      
      const { data, error } = await supabase
        .from('mensagens_chat')
        .insert([mensagem])
        .select()
        .single();
      
      if (error) {
        console.error('❌ Erro ao salvar mensagem da IA:', error);
        return null;
      }
      
      console.log('✅ Mensagem da IA salva:', data.id);
      return data.id;
      
    } catch (error) {
      console.error('❌ Erro ao salvar mensagem da IA:', error);
      return null;
    }
  };
  
  const salvarAudioUsuario = async (mensagemId, transcricao) => {
    try {
      setIsSavingAudio(true);
      console.log('💾 Salvando áudio do usuário...');
      
      if (audioChunksRef.current.length === 0) {
        console.warn('⚠️ Nenhum chunk de áudio capturado');
        return;
      }
      
      // Criar blob do áudio
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      
      // Salvar usando o service
      const result = await audiosChatService.salvarAudioUsuario(
        chatId,
        mensagemId,
        aluno.id,
        audioBlob,
        transcricao,
        {
          duracao: audioBlob.size / 16000, // Estimativa
          sampleRate: 16000,
          confidence: 0.9
        }
      );
      
      if (result.success) {
        console.log('✅ Áudio do usuário salvo com sucesso:', result.audioId);
      } else {
        console.error('❌ Erro ao salvar áudio do usuário:', result.error);
      }
      
      // Limpar chunks após salvar
      audioChunksRef.current = [];
      
    } catch (error) {
      console.error('❌ Erro ao salvar áudio do usuário:', error);
    } finally {
      setIsSavingAudio(false);
    }
  };
  
  const salvarAudioIA = async () => {
    try {
      console.log('🤖 Tentando salvar áudio da IA...');
      
      if (!currentMensagemId) {
        console.warn('⚠️ ID da mensagem da IA não encontrado');
        return;
      }
      
      // Por enquanto, a OpenAI Realtime API não fornece o áudio de resposta diretamente
      // Isso seria uma melhoria futura quando a API permitir capturar o áudio gerado
      console.log('ℹ️ Salvamento de áudio da IA não implementado ainda (limitação da API)');
      
    } catch (error) {
      console.error('❌ Erro ao salvar áudio da IA:', error);
    }
  };

  // Parar conversa (WebRTC ou HTTP)
  const stopConversation = () => {
    console.log('🛑 Parando conversa...');

    // Parar gravação se estiver ativa
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }

    // Fechar data channel primeiro (antes de fechar a conexão)
    if (dataChannel && dataChannel.readyState === 'open') {
      try {
        console.log('🔌 Fechando data channel...');
        dataChannel.close();
      } catch (err) {
        console.warn('⚠️ Erro ao fechar data channel:', err);
      }
    }
    setDataChannel(null);

    // Parar stream de áudio
    if (audioStream) {
      audioStream.getTracks().forEach(track => track.stop());
      setAudioStream(null);
    }

    // Fechar conexão WebRTC
    if (peerConnection) {
      try {
        peerConnection.close();
      } catch (err) {
        console.warn('⚠️ Erro ao fechar peer connection:', err);
      }
      setPeerConnection(null);
    }

    // Remover elementos de áudio do DOM
    const audioElements = document.querySelectorAll('audio[autoplay]');
    audioElements.forEach(el => {
      try {
        el.pause();
        el.srcObject = null;
        el.remove();
      } catch (err) {
        console.warn('⚠️ Erro ao remover elemento de áudio:', err);
      }
    });

    // Resetar estados
    setIsRecording(false);
    setIsConnected(false);
    setAudioRecorder(null);
    setIsAISpeaking(false);

    // Limpar estados de mensagem
    setCurrentUserMessage('');
    setCurrentAiMessage('');
    setCurrentMensagemId(null);

    // Limpar chunks de áudio
    audioChunksRef.current = [];

    setError(null);

    console.log('✅ Conversa encerrada com sucesso');
  };

  if (!isOpen) return null;

  return (
    <div className="voice-chat-overlay">
      <div className="voice-chat-popup">
        {/* Header */}
        <div className="voice-chat-header">
          <h3>🎤 Conversa com IA - {materia}</h3>
          <button className="btn-close" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        {/* Status */}
        <div className="voice-chat-status">
          <div className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
            <div className="status-dot"></div>
            <span>
              {isConnected ? '🟢 Conectado' : '🔴 Desconectado'}
            </span>
          </div>
        </div>

        {/* Avatar da IA */}
        <div className="ai-avatar-container">
          <img 
            src={isAISpeaking ? require('./avatar-talking.gif') : require('./avatar-static.jpg')} 
            alt={isAISpeaking ? 'IA falando' : 'IA ouvindo'}
            className={`ai-avatar ${isAISpeaking ? 'speaking' : 'listening'}`}
          />
          <div className="avatar-status">
            {isRecording && !isAISpeaking && (
              <div className="listening-indicator">
                <div className="pulse-dot"></div>
                <span>Ouvindo você...</span>
              </div>
            )}
            {isAISpeaking && (
              <div className="speaking-indicator">
                <div className="sound-waves">
                  <div className="wave"></div>
                  <div className="wave"></div>
                  <div className="wave"></div>
                </div>
                <span>IA falando...</span>
              </div>
            )}
          </div>
        </div>

        {/* Controles */}
        <div className="voice-chat-controls">
          {!isRecording ? (
            <button 
              className="btn-start-conversation"
              onClick={startSmartConversation}
              disabled={isProcessing || isDetectingEnvironment}
            >
              <MessageCircle size={24} />
              <span>
                {isDetectingEnvironment ? 'Detectando...' : 'Iniciar Conversa'}
                {chatMethod && (
                  <small style={{display: 'block', fontSize: '0.8em', opacity: 0.7}}>
                    {chatMethod.description}
                  </small>
                )}
              </span>
            </button>
          ) : (
            <>
              {chatMethod?.method === 'http' ? (
                // Controles para modo HTTP
                <button 
                  className="btn-record-http"
                  onClick={() => {
                    if (mediaRecorderRef.current?.state === 'recording') {
                      mediaRecorderRef.current.stop();
                    } else {
                      mediaRecorderRef.current?.start();
                      audioChunksRef.current = [];
                    }
                  }}
                  disabled={isProcessing}
                >
                  <MessageCircle size={24} />
                  <span>
                    {mediaRecorderRef.current?.state === 'recording' ? 'Parar Gravação' : 'Gravar Pergunta'}
                  </span>
                </button>
              ) : (
                // Controles para modo WebRTC (original)
                <button 
                  className="btn-stop-conversation"
                  onClick={stopConversation}
                >
                  <MicOff size={24} />
                  <span>Parar Conversa</span>
                </button>
              )}
            </>
          )}

          {isProcessing && (
            <div className="processing-indicator">
              <Loader size={20} className="spinning" />
              <span>Conectando...</span>
            </div>
          )}
        </div>

        {/* Status de Salvamento */}
        {isSavingAudio && (
          <div className="saving-audio-indicator">
            <Database size={16} className="spinning" />
            <span>Salvando áudio...</span>
          </div>
        )}

        {/* Histórico de Mensagens */}
        {messages.length > 0 && (
          <div className="voice-chat-messages">
            <h4>📝 Histórico da Conversa</h4>
            <div className="messages-container">
              {messages.map((msg) => (
                <div key={msg.id} className={`message ${msg.type}`}>
                  <div className="message-header">
                    <span className="message-sender">
                      {msg.type === 'user' ? '👤 Você' : '🤖 IA'}
                      {msg.hasAudio && <Save size={12} className="audio-saved-icon" />}
                    </span>
                    <span className="message-time">{msg.timestamp}</span>
                  </div>
                  <div className="message-content">
                    {msg.content}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Feedback */}
        {error && (
          <div className="voice-chat-feedback">
            {error}
          </div>
        )}

        {/* Instruções */}
        <div className="voice-chat-instructions">
          <p>💡 <strong>Como usar:</strong></p>
          {chatMethod?.method === 'http' ? (
            <ul>
              <li>🎤 Clique em "Iniciar Conversa" para começar</li>
              <li>🎙️ Clique em "Gravar Pergunta" e fale sobre {materia}</li>
              <li>⏹️ Clique em "Parar Gravação" quando terminar</li>
              <li>🎧 Ouça a resposta da IA automaticamente</li>
              <li>📝 Veja as transcrições no histórico</li>
              <li>🔄 Repita para fazer mais perguntas</li>
            </ul>
          ) : (
            <ul>
              <li>🎤 Clique em "Iniciar Conversa" para começar</li>
              <li>💬 Fale naturalmente sobre {materia}</li>
              <li>🎧 Ouça as respostas da IA em tempo real</li>
              <li>📝 Veja as transcrições aparecerem automaticamente</li>
              <li>⏹️ Clique em "Parar Conversa" para finalizar</li>
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleVoiceChat;
